/** Absolute Imports */
import React, { useEffect, useReducer, Suspense, lazy, useContext } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { Route, withRouter, Redirect, Switch } from 'react-router-dom'
import { injectIntl } from 'react-intl'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import IdleTimer from 'react-idle-timer'
import axios from 'axios'

/** Relative Imports */
import authAction from './actions/auth.actions'
import { getVoyage, isPastVoyage } from './actions/voyage.action'
import { getResourcesData } from './api/resources.api'
import { poststateroomData } from './api/stateroom.api'
import { postMasterData, getShipsData } from './api/masterdata.api'
import Navbar from './components/Navbar'
import FixedSidebar from './components/FixedSidebar'
import SessionTimeoutModal from './components/SessionTimeoutModal'
import ContainerShell from './components/ContainerShell'
import { getShip } from './actions/ship.action'
import { getGlobalSearchRoles } from './actions/search.action'
import { setModeAction } from './actions/mode.action'
import {
  isDefaultRouteVM,
  getShipTime,
  getCookieValue,
  mapAuthData,
  clearCookie,
  mapTokenInfo,
  setAuthDataInStore,
  initializeRefreshToken,
  getRefreshTokenObject,
  setLoggedInUserData,
  getAuthDataFromStorage,
  saveAuthToStorage,
  clearAuthFromStorage,
  getCurrentAppPath,
  getDefaultAppRoute,
  getSavedTabShipForDetailRoute,
  getVoyageToRestore,
  isAppRootPath,
  isPersonDetailRouteContextLoading,
  getTabActiveShip,
  isPageReload,
  isPersonDetailRoute,
  isShoreEnvironment,
  savePendingRoute,
  shouldFetchMasterdata
} from './helpers/util'

/** Relative Imports */
import { initAxios } from './helpers/api'
import { convertDateToDisplayDate, isDateBetween } from './helpers/dateUtil'

import routes from './constants/routes'
import {
  VIEW_BOARDING_MANAGEMENT,
  CREW_LIST,
  VISITOR_LIST,
  IPM_DUTIES_LIST,
  SETTINGS,
  GUEST_LIST,
  ALERT_LIST,
  MESSAGE_LIST,
  CREATE_ALERTS,
  CREATE_MESSAGES,
  VIEW_VISITORS,
  VIEW_ALERT_DETAILS,
  ADD_NEW_GUEST,
  UPDATE_EMBARK_DEBARK_DATE,
  VIEW_BOARDING_COUNT,
  VIEW_MESSAGE_DETAILS,
  VIEW_REPORTS,
  VIEW_EXCEPTION_REPORT,
  VIEW_LOCATION_REPORT,
  VIEW_LOCATION_HISTORY_REPORT,
  VIEW_SAILOR_FLIGHT_DETAILS_REPORT,
  VIEW_EMBARK_DEBARK_PORT_UPDATE_REPORT,
  VIEW_MOVEMENT_REPORT,
  VIEW_CBP_REPORT,
  VIEW_IPM_REPORT,
  STATEROOM_LIST,
  VIEW_CREATE_RESERVATION,
  GUEST_HISTORY,
  MOVE_BOARDING_SLOT,
  VIEW_FIDELIO_REPORT,
  VIEW_MASTER_TEMPLATE,
  VIEW_PASSENGERS_WITH_ALERTS_REPORT,
  VIEW_ONBOARD_GUEST_WITH_PAST_DEBARK_DATE_REPORT,
  authHandlerObject,
  APP_AUTH_NAME,
  VIEW_VOYAGE_PORT_TERMINALS,
  VIEW_PORT_TERMINALS,
  VIEW_BOARDING_SLOT_HISTORY_REPORT,
  VIEW_CBP_DASHBOARD,
  VISITOR_BULK_ACTIVATE,
  VIEW_BOARDING_SLOT_REPORT,
  VIEW_DOCUMENT_TYPE_BREAKDOWN_REPORT,
  VIEW_SAILOR_NATIONALITY_REPORT,
  VIEW_CREW_ROLES_MANAGEMENT
} from './constants'
import { getSettingsData } from './api/settings.api'
import { getSlotsData } from './api/slots.api'
import Loader from './components/Loader'
import Notification from './components/Notification'
import Login from './containers/Login'

/** Asset Imports */
import './App.scss'
import { ApolloContext } from './apolloClient'

const getSamlLogin = JSON.parse(get(window, '__APP_ENV__.samlLogin', 'false'))
const getContext = get(window, '__APP_ENV__.CONTEXT')

// Preload the component in advance that the user is likely to interact with next.
import('./containers/Dashboard')
import('./containers/BoardingSlots')
import('./containers/Crew')
import('./containers/Guests')
import('./containers/Visitors')
import('./containers/MasterTemplates')
import('./containers/AlertMessageList')
import('./containers/IPMLanding')
import('./containers/PortCoordinates')

/** Dynamic Routes */

const NewReservation = lazy(() => import('./containers/NewReservation'))
const IPMGroupList = lazy(() => import('./containers/IPMGroupList'))
const GuestHistory = lazy(() => import('./containers/GuestHistory'))
const GuestExceptionReportList = lazy(() =>
  import('./containers/GuestExceptionReportList')
)
const Stateroom = lazy(() => import('./containers/StateRoom'))
const Dashboard = lazy(() => import('./containers/Dashboard'))
const BoardingSlots = lazy(() => import('./containers/BoardingSlots'))
const SelectShip = lazy(() => import('./components/SelectShip'))
const Crew = lazy(() => import('./containers/Crew'))
const Guests = lazy(() => import('./containers/Guests'))
const Visitors = lazy(() => import('./containers/Visitors'))
const SearchResults = lazy(() => import('./containers/SearchResults'))
const GuestDetails = lazy(() => import('./containers/GuestDetails'))
const MasterTemplates = lazy(() => import('./containers/MasterTemplates'))
const CrewDetails = lazy(() => import('./containers/CrewDetails'))
const AlertMessageList = lazy(() => import('./containers/AlertMessageList'))
const CreateAlertMessage = lazy(() => import('./containers/CreateAlertMessage'))
const editAlert = lazy(() => import('./containers/CreateAlertMessage'))
const editMessage = lazy(() => import('./containers/CreateAlertMessage'))
const CbpDashboard = lazy(() => import('./containers/CbpDashboard'))
const CbpMessageSchedule = lazy(() => import('./containers/CbpMessageSchedule'))
const CbpVcoMessage = lazy(() => import('./containers/CbpVcoMessage'))
const CbpComparison = lazy(() => import('./containers/CbpComparison'))
const ReportsLanding = lazy(() => import('./containers/ReportsLanding'))
const VisitorDetail = lazy(() => import('./containers/VisitorDetail'))
const AlertMessageDetail = lazy(() => import('./containers/AlertMessageDetail'))
const IPMLanding = lazy(() => import('./containers/IPMLanding'))
const MovementReport = lazy(() => import('./containers/MovementReport'))
const ExceptionReport = lazy(() => import('./containers/ExceptionReport'))
const LocationReport = lazy(() => import('./containers/LocationReport'))
const IpmReport = lazy(() => import('./containers/IpmReport'))
const FidelioReports = lazy(() => import('./containers/FidelioReports'))
const InActiveVisitorReport = lazy(() =>
  import('./containers/InActiveVisitorReport')
)
const PassengersWithAlertsReports = lazy(() =>
  import('./containers/PassengersWithAlertsReports')
)
const OnboardGuestWithPastDebarkDateReport = lazy(() =>
  import('./containers/OnboardGuestWithPastDebarkDateReport')
)
const LocationHistoryReport = lazy(() =>
  import('./containers/LocationHistoryReport')
)
const SailorFlightDetailsReports = lazy(() =>
  import('./containers/SailorFlightDetailsReports')
)
const EmbarkDebarkPortUpdateReport = lazy(() =>
  import('./containers/EmbarkDebarkPortUpdateReport')
)
const BoardingSlotHistoryReport = lazy(() =>
  import('./containers/BoardingSlotHistoryReport')
)
const CbpReport = lazy(() => import('./containers/CbpReport'))
const Settings = lazy(() => import('./containers/Settings'))
const PortCoordinates = lazy(() => import('./containers/PortCoordinates'))
const PortMasterData = lazy(() =>
  import('./containers/PortCoordinates/PortMasterData')
)
const BoardingSlotReport = lazy(() => import('./containers/BoardingSlotReport'))
const ClearanceReportsLanding = lazy(() =>
  import('./containers/ClearanceReportsLanding')
)
const DocumentTypeBreakdownReport = lazy(() =>
  import('./containers/ClearanceReport/DocumentTypeBreakdownReport')
)
const SailorNationalityReport = lazy(() =>
  import('./containers/ClearanceReport/SailorNationalityReport')
)
const SailorAshoreVisitReport = lazy(() =>
  import('./containers/ClearanceReport/SailorAshoreVisitReport')
)
const GenderDistributionReport = lazy(() =>
  import('./containers/ClearanceReport/GenderDistributionReport')
)
const CrewRolesManagement = lazy(() =>
  import('./containers/CrewRolesManagement')
)
const CrewListingManagement = lazy(() =>
  import('./containers/CrewRolesManagement/CrewListingManagement')
)
const CrewRolesMapping = lazy(() =>
  import('./containers/CrewRolesManagement/CrewRolesMapping')
)
const CrewRolesFeatureMapping = lazy(() =>
  import('./containers/CrewRolesManagement/CrewRolesFeatureMapping')
)
const CrewAddUser = lazy(() =>
  import('./containers/CrewRolesManagement/CrewAddUser')
)
const getInitialState = () => ({
  shouldShowGuestList: false,
  shouldShowCrewList: false,
  shouldShowBoardingSlots: false,
  shouldShowVisitorList: false,
  shouldShowIPMDutiesList: false,
  shouldShowSettings: false,
  shouldShowAlertList: false,
  shouldShowMessageList: false,
  shouldCreateAlerts: false,
  shouldCreateMessages: false,
  shouldEditMessages: false,
  shouldEditAlerts: false,
  shouldShowVisitorDetails: false,
  shouldShowAlertDetails: false,
  shouldShowMessageDetails: false,
  shouldShowReportList: false,
  shouldShowExceptionReportList: false,
  shouldShowLocationReportList: false,
  shouldShowLocationHistoryReportList: false,
  shouldShowSailorFlightDetailsReportList: false,
  shouldShowEmbarkDebarkPortUpdateReportList: false,
  shouldShowBoardingSlotHistoryReportList: false,
  shouldShowBoardingSlotReport: true,
  shouldShowMovementReportList: false,
  shouldShowInActiveReportList: false,
  shouldShowDocumentTypeBreakdownReportList: false,
  shouldShowSailorNationalityReportList: false,
  shouldShowCbpReportList: false,
  shouldShowIpmReportList: false,
  shouldShowFidelioReportList: false,
  ShouldShowSessionTimeoutModal: false,
  shouldShowDashboard: true,
  shouldShowStateroom: false,
  shouldShowBoardingCount: false,
  shouldShowCreateReservation: false,
  shouldShowMasterTemplate: false,
  shouldShowGuestHistory: false,
  shouldShowVoyagePortTerminals: false,
  shouldShowPortTerminals: false,
  shouldUpdateEmbarkDebarkDate: false,
  shouldShowCbpDashboard: false,
  shouldShowCrewRolesManagement: false,
  idleTimer: null
})

/**
 * App Component
 * @param {props} param0 props
 */
const App = ({
  actions,
  match,
  history,
  auth,
  startup,
  loader,
  masterdata,
  intl,
  activeVoyage,
  activeShip,
  isShore,
  ships,
  activeShipName,
  settings,
  latestBoardingSlot,
  totalActiveSlotsCount,
  shipTimes
}) => {
  const [state, setState] = useReducer(
    (state, nextState) => ({ ...state, ...nextState }),
    getInitialState()
  )

  const redirectSamLoginAction = () => {
    const authCookieName = 'auth'
    const userCookieName = 'user'
    const context = get(window, '__APP_ENV__.CONTEXT')
    let pathname = window.location.pathname + window.location.search
    pathname = pathname.replace(`${context}`, '').replace('//', '/')
    const authCookie = getCookieValue(authCookieName)
    const userCookie = getCookieValue(userCookieName)
    if (!authCookie || authCookie == '') {
      const encoded = encodeURIComponent(
        `${window.location.origin}/${context}${pathname}`
      )
      window.location = `${window.location.origin}/${context}/slogin?redirectUri=${encoded}`
    } else {
      const authData = mapAuthData({
        auth: decodeURIComponent(authCookie),
        user: decodeURIComponent(userCookie)
      })
      clearCookie(authCookieName)
      clearCookie(userCookieName)
      initializeRefreshToken(authData)
      setLoggedInUserData(authData)
      axios({
        method: 'get',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${get(
            authData,
            'tokenDetail.accessToken',
            ''
          )}`
        },
        url: `${window.location.origin}/${context}/roles`
      })
        .then(response => {
          const { data } = response
          authData.userProfileDetail['roleDetail'] = data
          saveAuthToStorage(authData, true)
          actions.auth(authData)
        })
        .catch(() => {
          actions.hideLoader()
        })
    }
  }
  /** Component Did Mount */
  useEffect(() => {
    const authData = getAuthDataFromStorage()
    if ((window.location.pathname || '').includes('login')) {
      history.push(`${match.path}/${routes.login}`)
    } else if (!isEmpty(authData)) {
      initializeRefreshToken(authData)
      actions.auth(authData)
      setLoggedInUserData(authData)
      const tabShip = getTabActiveShip()
      if (tabShip) {
        actions.setActiveShip(tabShip)
      }
    } else {
      if (!getSamlLogin) {
        history.push(`${match.path}/${routes.login}`)
      } else {
        redirectSamLoginAction()
      }
    }
  }, [])
  useEffect(() => {
    if (
      !isEmpty(get(auth, 'tokenDetail.accessToken')) &&
      !get(authHandlerObject, 'authHandler', false)
    ) {
      authHandlerObject.authHandler = getRefreshTokenObject(auth)
    }
  }, [get(auth, 'tokenDetail.accessToken', [])])
  const isDefaultRouteVMEnable = isDefaultRouteVM(
    get(auth, 'userProfileDetail.roleDetail', [])
  )

  let { ShouldShowSessionTimeoutModal } = state
  const onIdle = () => {
    setState({ ShouldShowSessionTimeoutModal: true })
  }
  const handleOnActiveUser = () => {
    if (ShouldShowSessionTimeoutModal) {
      setState({ ShouldShowSessionTimeoutModal: false })
    }
  }

  const reLoginFun = () => {
    clearAuthFromStorage()
    localStorage.removeItem('roles')
    sessionStorage.removeItem('roles')
    localStorage.removeItem(APP_AUTH_NAME)
    if (!getSamlLogin) {
      window.location.href = `${match.path}/${routes.login}`
    } else {
      const encoded = encodeURIComponent(
        `${window.location.origin}/${getContext}/`
      )
      window.location = `${window.location.origin}/${getContext}/slogout?redirectUri=${encoded}`
    }
  }

  /** Startup call on authDetails change */
  useEffect(() => {
    let shouldShowCrewList = false
    let shouldShowGuestList = false
    let shouldShowVisitorList = false
    if (!isEmpty(get(auth, 'tokenDetail.accessToken'))) {
      initAxios(get(auth, 'tokenDetail.accessToken'))

      get(
        auth,
        'userProfileDetail.roleDetail[0].applicationFeatures',
        []
      ).forEach(feature => {
        switch ((feature.code || '').toUpperCase()) {
          case CREW_LIST:
            shouldShowCrewList = true
            break
          case GUEST_LIST:
            shouldShowGuestList = true
            break
          case VIEW_BOARDING_MANAGEMENT:
            setState({ shouldShowBoardingSlots: true })
            break
          case VISITOR_LIST:
            shouldShowVisitorList = true
            break
          case IPM_DUTIES_LIST:
            setState({ shouldShowIPMDutiesList: true })
            break
          case SETTINGS:
            setState({ shouldShowSettings: true })
            break
          case ALERT_LIST:
            setState({ shouldShowAlertList: true })
            break
          case MESSAGE_LIST:
            setState({ shouldShowMessageList: true })
            break
          case CREATE_ALERTS:
            setState({ shouldCreateAlerts: true, shouldEditAlerts: true })
            break
          case CREATE_MESSAGES:
            setState({ shouldCreateMessages: true, shouldEditMessages: true })
            break
          case VIEW_VISITORS:
            setState({ shouldShowVisitorDetails: true })
            break
          case VIEW_ALERT_DETAILS:
            setState({ shouldShowAlertDetails: true })
            break
          case ADD_NEW_GUEST:
            setState({ shouldShowAddGuest: true })
            break
          case UPDATE_EMBARK_DEBARK_DATE:
            setState({ shouldUpdateEmbarkDebarkDate: true })
            break
          case VIEW_MESSAGE_DETAILS:
            setState({ shouldShowMessageDetails: true })
            break
          case VIEW_REPORTS:
            setState({ shouldShowReportList: true })
            break
          case VIEW_EXCEPTION_REPORT:
            setState({ shouldShowExceptionReportList: true })
            break
          case VIEW_LOCATION_REPORT:
            setState({ shouldShowLocationReportList: true })
            break
          case VIEW_LOCATION_HISTORY_REPORT:
            setState({ shouldShowLocationHistoryReportList: true })
            break
          case VIEW_SAILOR_FLIGHT_DETAILS_REPORT:
            setState({ shouldShowSailorFlightDetailsReportList: true })
            break
          case VIEW_EMBARK_DEBARK_PORT_UPDATE_REPORT:
            setState({ shouldShowEmbarkDebarkPortUpdateReportList: true })
            break
          case VIEW_BOARDING_SLOT_HISTORY_REPORT:
            setState({ shouldShowBoardingSlotHistoryReportList: true })
            break
          case VIEW_BOARDING_SLOT_REPORT:
            setState({ shouldShowBoardingSlotReport: true })
            break
          case VIEW_MOVEMENT_REPORT:
            setState({ shouldShowMovementReportList: true })
            break
          case VISITOR_BULK_ACTIVATE:
            setState({ shouldShowInActiveReportList: true })
            break
          case VIEW_CBP_REPORT:
            setState({ shouldShowCbpReportList: true })
            break
          case VIEW_IPM_REPORT:
            setState({ shouldShowIpmReportList: true })
            break
          case STATEROOM_LIST:
            setState({ shouldShowStateroom: true })
            break
          case VIEW_CREATE_RESERVATION:
            setState({ shouldShowCreateReservation: true })
            break
          case VIEW_MASTER_TEMPLATE:
            setState({ shouldShowMasterTemplate: true })
            break
          case VIEW_BOARDING_COUNT:
            setState({ shouldShowBoardingCount: true })
            break
          case GUEST_HISTORY:
            setState({ shouldShowGuestHistory: true })
            break
          case MOVE_BOARDING_SLOT:
            setState({ shouldShowMoveBoardingSlot: true })
            break
          case VIEW_FIDELIO_REPORT:
            setState({ shouldShowFidelioReportList: true })
            break
          case VIEW_PASSENGERS_WITH_ALERTS_REPORT:
            setState({ shouldShowPassengersWithAlertsReportList: true })
            break
          case VIEW_ONBOARD_GUEST_WITH_PAST_DEBARK_DATE_REPORT:
            setState({ shouldShowOnboardGuestWithPastDebarkDateReport: true })
            break
          case VIEW_DOCUMENT_TYPE_BREAKDOWN_REPORT:
            setState({ shouldShowDocumentTypeBreakdownReportList: true })
            break
          case VIEW_SAILOR_NATIONALITY_REPORT:
            setState({ shouldShowSailorNationalityReportList: true })
            break
          case VIEW_VOYAGE_PORT_TERMINALS:
            setState({ shouldShowVoyagePortTerminals: true })
            break
          case VIEW_PORT_TERMINALS:
            setState({ shouldShowPortTerminals: true })
            break
          case VIEW_CBP_DASHBOARD:
            setState({ shouldShowCbpDashboard: true })
            break
          case VIEW_CREW_ROLES_MANAGEMENT:
            setState({ shouldShowCrewRolesManagement: true })
            break
        }
      })
      setState({
        shouldShowDashboard: !isDefaultRouteVMEnable,
        shouldShowVisitorList,
        shouldShowGuestList,
        shouldShowCrewList
      })
      actions.getGlobalRoles({
        shouldShowCrewList,
        shouldShowGuestList,
        shouldShowVisitorList
      })
      actions.configs()
      if ((window.location.pathname || '').includes('login')) {
        history.push(getDefaultAppRoute(match.path, isDefaultRouteVMEnable))
      }
    }
  }, [
    get(auth, 'tokenDetail.accessToken', ''),
    get(auth, 'userProfileDetail.roleDetail[0].applicationFeatures')
  ])

  useEffect(() => {
    // if (samlLogin) {
    //   redirectSamLogin()
    // }
    window.addEventListener('refreshToken', event => {
      if (get(event, ['detail', 'isNewToken'], false)) {
        let authDataResponse = get(event, ['detail', 'data'], {})
        if (!isEmpty(authDataResponse)) {
          const tokenData = mapTokenInfo(authDataResponse)
          initAxios(get(tokenData, 'accessToken'))
          actions.auth({ tokenDetail: tokenData })
          setAuthDataInStore(tokenData, 'auth')
        }
      }
    })
  }, [])
  const { isClientWithUrlSet } = useContext(ApolloContext)

  useEffect(() => {
    if (isEmpty(settings) || isEmpty(startup)) {
      return
    }

    const currentPath = getCurrentAppPath()

    if (isShoreEnvironment(settings)) {
      actions.setMode(true)
      const tabShip = getSavedTabShipForDetailRoute(activeShip, currentPath)
      if (tabShip) {
        actions.setActiveShip(tabShip)
      }
    }

    if (!isClientWithUrlSet) {
      return
    }

    actions.getResources()
    if (isShoreEnvironment(settings)) {
      actions.getShips()
    } else {
      actions.setActiveShip(get(settings, 'ship.code', ''))
    }
  }, [settings, startup, isClientWithUrlSet, activeShip])

  useEffect(() => {
    const isShoreSide = isShore || isShoreEnvironment(settings)
    if (!isShoreSide || ships.length === 0 || !isEmpty(activeShip)) {
      return
    }

    const currentPath = getCurrentAppPath()
    const tabShip = getSavedTabShipForDetailRoute(activeShip, currentPath)
    if (tabShip) {
      actions.setActiveShip(tabShip)
      return
    }

    if (!currentPath.includes(routes.selectShip)) {
      if (!isPageReload() && isPersonDetailRoute(currentPath)) {
        savePendingRoute(currentPath)
      }
      history.push(`${match.path}/${routes.selectShip}`)
    }
  }, [ships, isShore, settings, activeShip])

  useEffect(() => {
    if (
      !shouldFetchMasterdata({
        activeShip,
        settings,
        startup,
        isClientWithUrlSet,
        isShore
      })
    ) {
      return
    }

    actions.masterdata({
      shipCode: activeShip
    })
  }, [activeShip, isShore, settings, startup, isClientWithUrlSet])

  useEffect(() => {
    if (isEmpty(masterdata) || isEmpty(activeShip) || !isEmpty(activeVoyage)) {
      return
    }

    const isShoreSide = isShore || isShoreEnvironment(settings)
    const currentPath = getCurrentAppPath()
    const restoreResult = getVoyageToRestore(
      masterdata,
      activeShip,
      isShoreSide
    )

    if (restoreResult) {
      if (restoreResult.isPastVoyage) {
        actions.isPastVoyage(true)
      }
      actions.setActiveVoyage(restoreResult.voyage)
    }

    if (
      isPersonDetailRoute(currentPath) ||
      !isAppRootPath(currentPath, match.path)
    ) {
      return
    }

    history.push(getDefaultAppRoute(match.path, isDefaultRouteVMEnable))
  }, [masterdata, activeShip, activeVoyage, isShore, settings])

  useEffect(() => {
    if (!isEmpty(activeVoyage)) {
      actions.getSlotsData()
    }
  }, [activeVoyage])

  const {
    shouldShowAlertList,
    shouldShowMessageList,
    shouldShowCrewList,
    shouldShowGuestList,
    shouldShowVisitorList,
    shouldShowBoardingSlots,
    shouldShowIPMDutiesList,
    shouldShowSettings,
    shouldCreateAlerts,
    shouldCreateMessages,
    shouldEditMessages,
    shouldEditAlerts,
    shouldShowVisitorDetails,
    shouldShowAlertDetails,
    shouldShowMessageDetails,
    shouldShowReportList,
    shouldShowExceptionReportList,
    shouldShowLocationReportList,
    shouldShowLocationHistoryReportList,
    shouldShowSailorFlightDetailsReportList,
    shouldShowEmbarkDebarkPortUpdateReportList,
    shouldShowBoardingSlotHistoryReportList,
    shouldShowBoardingSlotReport,
    shouldShowMovementReportList,
    shouldShowCbpReportList,
    shouldShowIpmReportList,
    shouldShowFidelioReportList,
    shouldShowDashboard,
    shouldShowStateroom,
    shouldShowBoardingCount,
    shouldShowCreateReservation,
    shouldShowMasterTemplate,
    shouldShowPassengersWithAlertsReportList,
    shouldShowOnboardGuestWithPastDebarkDateReport,
    shouldShowGuestHistory,
    shouldShowVoyagePortTerminals,
    shouldShowPortTerminals,
    shouldShowCbpDashboard,
    shouldShowInActiveReportList,
    shouldShowDocumentTypeBreakdownReportList,
    shouldShowSailorNationalityReportList,
    shouldShowCrewRolesManagement
  } = state

  //New Reservation Feature must only be available for the active voyage not on past or future voyage
  useEffect(() => {
    if (get(activeVoyage, 'debarkDate', false) && getShipTime(shipTimes)) {
      const isShowbutton = isDateBetween(
        getShipTime(shipTimes),
        activeVoyage.embarkDate,
        activeVoyage.debarkDate
      )
      if (shouldShowCreateReservation) {
        if (!isShowbutton) {
          setState({ shouldShowCreateReservation: false })
        }
      } else {
        if (
          isShowbutton &&
          get(
            auth,
            'userProfileDetail.roleDetail[0].applicationFeatures',
            []
          ).some(
            applicationFeature =>
              (applicationFeature.code || '').toUpperCase() ===
              VIEW_CREATE_RESERVATION
          )
        ) {
          setState({ shouldShowCreateReservation: true })
        }
      }
    }
  }, [activeVoyage])

  const showPersonDetailRouteContextLoader = isPersonDetailRouteContextLoading({
    currentPath: getCurrentAppPath(),
    accessToken: get(auth, 'tokenDetail.accessToken'),
    activeShip,
    startup,
    masterdata,
    activeVoyage
  })

  /** Render block */
  return (
    <div className="RootContainer">
      {(loader > 0 || showPersonDetailRouteContextLoader) && <Loader />}
      <Notification />
      <React.Fragment>
        <Route path={`${match.path}/${routes.login}`} exact component={Login} />
        <Suspense fallback={<ContainerShell />}>
          <Route
            path={`${match.path}/${routes.selectShip}`}
            component={SelectShip}
          />
        </Suspense>

        {window.location.pathname !== `${match.path}/${routes.login}` &&
          !isEmpty(activeShip) &&
          !isEmpty(get(auth, 'tokenDetail.accessToken', '')) && (
            <>
              {get(settings, 'autoLogoutEnable', true) && (
                <>
                  <IdleTimer
                    element={document}
                    onIdle={onIdle}
                    onActive={handleOnActiveUser}
                    timeout={
                      1000 * 60 * get(settings, 'logoutSessionTimeout', 15)
                    }
                  />
                  {ShouldShowSessionTimeoutModal && (
                    <SessionTimeoutModal
                      isOpen={ShouldShowSessionTimeoutModal}
                      onDismissAction={() => {}}
                      onReLoginAction={reLoginFun}
                    />
                  )}
                </>
              )}
              <Navbar
                title={intl.formatMessage({
                  id: 'App.navbarTitle',
                  defaultMessage: 'Supervisor'
                })}
                searchText={intl.formatMessage({
                  id: 'App.searchText',
                  defaultMessage: 'Search'
                })}
                logoutText={intl.formatMessage({
                  id: 'App.logoutText',
                  defaultMessage: 'Logout'
                })}
                auth={auth}
                isShore={isShore}
                shipName={activeShipName}
                voyageName={`${activeVoyage.voyageName ||
                  ''} ${convertDateToDisplayDate(
                  activeVoyage.embarkDate
                )}-${convertDateToDisplayDate(activeVoyage.debarkDate)}`}
                shouldShowCrewList={shouldShowCrewList}
                shouldShowGuestList={shouldShowGuestList}
                shouldShowIPMDutiesList={shouldShowIPMDutiesList}
                shouldShowStateroom={shouldShowStateroom}
                shouldShowBoardingSlots={shouldShowBoardingSlots}
                shouldShowBoardingCount={shouldShowBoardingCount}
                shouldShowVisitorList={shouldShowVisitorList}
                shouldShowCreateReservation={shouldShowCreateReservation}
                shouldShowMasterTemplate={shouldShowMasterTemplate}
                shouldShowGuestHistory={shouldShowGuestHistory}
                shouldShowSettings={shouldShowSettings}
                shouldShowAlertList={shouldShowAlertList}
                shouldShowMessageList={shouldShowMessageList}
                shouldShowReportList={shouldShowReportList}
                shouldShowCbpDashboard={shouldShowCbpDashboard}
                shouldShowDashboard={shouldShowDashboard}
                shouldShowVoyagePortTerminals={shouldShowVoyagePortTerminals}
                shouldShowPortTerminals={shouldShowPortTerminals}
                shouldShowCrewRolesManagement={shouldShowCrewRolesManagement}
                settings={settings}
                latestBoardingSlot={latestBoardingSlot}
                totalActiveSlotsCount={totalActiveSlotsCount}
                isShipMode={get(startup, 'shipCode')}
              />
              <div className="LayoutContainer">
                <FixedSidebar
                  isShore={isShore}
                  shipName={activeShipName}
                  voyageName={`${activeVoyage.voyageName ||
                    ''} ${convertDateToDisplayDate(
                    activeVoyage.embarkDate
                  )}-${convertDateToDisplayDate(activeVoyage.debarkDate)}`}
                  shouldShowCrewList={shouldShowCrewList}
                  shouldShowGuestList={shouldShowGuestList}
                  shouldShowIPMDutiesList={shouldShowIPMDutiesList}
                  shouldShowBoardingCount={shouldShowBoardingCount}
                  shouldShowBoardingSlots={shouldShowBoardingSlots}
                  shouldShowVisitorList={shouldShowVisitorList}
                  shouldShowSettings={shouldShowSettings}
                  shouldShowAlertList={shouldShowAlertList}
                  shouldShowMessageList={shouldShowMessageList}
                  shouldShowCreateReservation={shouldShowCreateReservation}
                  shouldShowMasterTemplate={shouldShowMasterTemplate}
                  shouldShowGuestHistory={shouldShowGuestHistory}
                  shouldShowReportList={shouldShowReportList}
                  shouldShowCbpDashboard={shouldShowCbpDashboard}
                  shouldShowDashboard={shouldShowDashboard}
                  shouldShowStateroom={shouldShowStateroom}
                  shouldShowVoyagePortTerminals={shouldShowVoyagePortTerminals}
                  shouldShowPortTerminals={shouldShowPortTerminals}
                  shouldShowCrewRolesManagement={shouldShowCrewRolesManagement}
                  settings={settings}
                  latestBoardingSlot={latestBoardingSlot}
                  totalActiveSlotsCount={totalActiveSlotsCount}
                  isShipMode={get(startup, 'shipCode')}
                />
                <div className="PageContainer">
                  <Suspense fallback={<ContainerShell />}>
                    <Switch>
                      <Route
                        exact
                        path={`${match.path}/`}
                        render={() => (
                          <Redirect
                            to={
                              isDefaultRouteVMEnable
                                ? `${match.path}/${routes.visitors}`
                                : `${match.path}/${routes.dashboard}`
                            }
                          />
                        )}
                      />
                      {shouldShowCreateReservation && (
                        <Route
                          exact
                          path={`${match.path}/${routes.newReservation}`}
                          component={NewReservation}
                        />
                      )}
                      {shouldShowStateroom &&
                        get(settings, 'sidebar.shouldShowStateroom', false) && (
                          <Route
                            exact
                            path={`${match.path}/${routes.stateroom}`}
                            component={Stateroom}
                          />
                        )}
                      <Route
                        exact
                        path={`${match.path}/${routes.dashboard}`}
                        component={Dashboard}
                      />
                      {shouldShowCrewRolesManagement && (
                        <Route
                          exact
                          path={`${match.path}/${routes.crewRolesManagement}`}
                          component={CrewRolesManagement}
                        />
                      )}
                      {shouldShowCrewRolesManagement && (
                        <Route
                          exact
                          path={`${match.path}/${routes.crewListingManagement}`}
                          component={CrewListingManagement}
                        />
                      )}
                      {shouldShowCrewRolesManagement && (
                        <Route
                          exact
                          path={`${match.path}/${routes.crewRolesMapping}`}
                          component={CrewRolesMapping}
                        />
                      )}
                      {shouldShowCrewRolesManagement && (
                        <Route
                          exact
                          path={`${match.path}/${routes.crewRolesFeatureMapping}`}
                          component={CrewRolesFeatureMapping}
                        />
                      )}
                      {shouldShowCrewRolesManagement && (
                        <Route
                          exact
                          path={`${match.path}/${routes.crewAddUser}`}
                          component={CrewAddUser}
                        />
                      )}
                      {(shouldShowBoardingSlots || shouldShowBoardingCount) &&
                        get(
                          settings,
                          'dashboard.shouldShowBoardingSlots',
                          false
                        ) && (
                          <Route
                            exact
                            path={`${match.path}/${routes.boardingSlots}`}
                            component={BoardingSlots}
                          />
                        )}
                      {get(
                        settings,
                        'dashboard.shouldShowMasterTemplates',
                        false
                      ) &&
                        shouldShowMasterTemplate &&
                        isShore && (
                          <Route
                            exact
                            path={`${match.path}/${routes.masterTemplates}`}
                            component={MasterTemplates}
                          />
                        )}
                      {shouldShowCrewList &&
                        !isShore &&
                        get(settings, 'sidebar.shouldShowCrewList', false) && (
                          <Route
                            exact
                            path={`${match.path}/${routes.crew}`}
                            component={Crew}
                          />
                        )}
                      {shouldShowGuestList &&
                        get(settings, 'sidebar.shouldShowGuestList', false) && (
                          <Route
                            exact
                            path={`${match.path}/${routes.guests}`}
                            component={Guests}
                          />
                        )}
                      {shouldShowVisitorList &&
                        get(
                          settings,
                          'sidebar.shouldShowVisitorList',
                          false
                        ) && (
                          <Route
                            exact
                            path={`${match.path}/${routes.visitors}`}
                            component={Visitors}
                          />
                        )}
                      <Route
                        exact
                        path={`${match.path}/${routes.results}`}
                        component={SearchResults}
                      />
                      <Route
                        exact
                        path={`${match.path}/${routes.guest}/:guestId`}
                        component={GuestDetails}
                      />
                      <Route
                        exact
                        path={`${match.path}/${routes.crew}/:crewId`}
                        component={CrewDetails}
                      />
                      {shouldShowGuestHistory && (
                        <Route
                          exact
                          path={`${match.path}/${routes.guest}/:guestId/${routes.guestHistory}`}
                          component={GuestHistory}
                        />
                      )}
                      {
                        <Route
                          exact
                          path={`${match.path}/boarding/exception-report`}
                          component={GuestExceptionReportList}
                        />
                      }
                      {
                        <Route
                          exact
                          path={`${match.path}/ipm/group`}
                          component={IPMGroupList}
                        />
                      }
                      {(shouldShowAlertList ||
                        (shouldShowMessageList &&
                          get(
                            settings,
                            'messages.shouldEnableMessagesFeature',
                            false
                          ))) && (
                        <Route
                          exact
                          path={`${match.path}/${routes.alertsAndMessages}`}
                          component={AlertMessageList}
                        />
                      )}
                      {shouldCreateAlerts && (
                        <Route
                          exact
                          path={`${match.path}/${routes.createAlerts}`}
                          component={CreateAlertMessage}
                        />
                      )}
                      {shouldCreateMessages &&
                        get(
                          settings,
                          'messages.shouldEnableMessagesFeature',
                          false
                        ) && (
                          <Route
                            exact
                            path={`${match.path}/${routes.createMessage}`}
                            component={CreateAlertMessage}
                          />
                        )}
                      {shouldEditAlerts &&
                        get(
                          settings,
                          'messages.shouldEnableMessagesFeature',
                          false
                        ) && (
                          <Route
                            exact
                            path={`${match.path}/${routes.editAlert}`}
                            component={editAlert}
                          />
                        )}
                      {shouldEditMessages &&
                        get(
                          settings,
                          'messages.shouldEnableMessagesFeature',
                          false
                        ) && (
                          <Route
                            exact
                            path={`${match.path}/${routes.editMessage}`}
                            component={editMessage}
                          />
                        )}
                      {shouldShowReportList &&
                        get(
                          settings,
                          'sidebar.shouldShowReportList',
                          false
                        ) && (
                          <Route
                            exact
                            path={`${match.path}/${routes.reports}`}
                            component={ReportsLanding}
                          />
                        )}
                      {shouldShowVisitorDetails && (
                        <Route
                          exact
                          path={`${match.path}/${routes.visitorDetail}`}
                          component={VisitorDetail}
                        />
                      )}
                      {shouldShowAlertDetails && (
                        <Route
                          exact
                          path={`${match.path}/${routes.alertDetail}`}
                          component={AlertMessageDetail}
                        />
                      )}
                      {shouldShowMessageDetails &&
                        get(
                          settings,
                          'messages.shouldEnableMessagesFeature',
                          false
                        ) && (
                          <Route
                            exact
                            path={`${match.path}/${routes.messageDetail}`}
                            component={AlertMessageDetail}
                          />
                        )}
                      {shouldShowIPMDutiesList &&
                        get(
                          settings,
                          'sidebar.shouldShowIpmDutiesList',
                          false
                        ) && (
                          <Route
                            exact
                            path={`${match.path}/${routes.ipmLanding}`}
                            component={IPMLanding}
                          />
                        )}
                      {shouldShowSettings &&
                        get(
                          settings,
                          'sidebar.shouldShowSettingList',
                          false
                        ) && (
                          <Route
                            exact
                            path={`${match.path}/${routes.settings}`}
                            component={Settings}
                          />
                        )}
                      {shouldShowVoyagePortTerminals && (
                        <Route
                          exact
                          path={`${match.path}/${routes.portCoordinates}`}
                          component={PortCoordinates}
                        />
                      )}
                      {shouldShowPortTerminals && (
                        <Route
                          exact
                          path={`${match.path}/${routes.portMasterData}`}
                          component={PortMasterData}
                        />
                      )}
                      {shouldShowReportList && shouldShowMovementReportList && (
                        <Route
                          exact
                          path={`${match.path}/${routes.movementReports}`}
                          component={MovementReport}
                        />
                      )}
                      {shouldShowReportList && (
                        <Route
                          exact
                          path={`${match.path}/${routes.clearanceReports}`}
                          component={ClearanceReportsLanding}
                        />
                      )}
                      {shouldShowReportList &&
                        shouldShowDocumentTypeBreakdownReportList && (
                          <Route
                            exact
                            path={`${match.path}/${routes.documentTypeBreakdownReport}`}
                            component={DocumentTypeBreakdownReport}
                          />
                        )}
                      {shouldShowReportList &&
                        shouldShowSailorNationalityReportList && (
                          <Route
                            exact
                            path={`${match.path}/${routes.sailorNationalityReport}`}
                            component={SailorNationalityReport}
                          />
                        )}
                      {shouldShowReportList && (
                        <Route
                          exact
                          path={`${match.path}/${routes.sailorAshoreVisitReport}`}
                          component={SailorAshoreVisitReport}
                        />
                      )}
                      {shouldShowReportList && (
                        <Route
                          exact
                          path={`${match.path}/${routes.genderDistributionByNationalityReport}`}
                          component={GenderDistributionReport}
                        />
                      )}
                      {shouldShowReportList && shouldShowInActiveReportList && (
                        <Route
                          exact
                          path={`${match.path}/${routes.inactiveVisitors}`}
                          component={InActiveVisitorReport}
                        />
                      )}
                      {shouldShowReportList &&
                        shouldShowExceptionReportList && (
                          <Route
                            exact
                            path={`${match.path}/${routes.exceptionReports}`}
                            component={ExceptionReport}
                          />
                        )}
                      {shouldShowReportList && shouldShowLocationReportList && (
                        <Route
                          exact
                          path={`${match.path}/${routes.locationReports}`}
                          component={LocationReport}
                        />
                      )}
                      {shouldShowLocationHistoryReportList && (
                        <Route
                          exact
                          path={`${match.path}/${routes.locationHistoryReports}`}
                          component={LocationHistoryReport}
                        />
                      )}
                      {isShore && shouldShowSailorFlightDetailsReportList && (
                        <Route
                          exact
                          path={`${match.path}/${routes.sailorFlightDetailsReports}`}
                          component={SailorFlightDetailsReports}
                        />
                      )}
                      {!isShore &&
                        shouldShowReportList &&
                        shouldShowEmbarkDebarkPortUpdateReportList && (
                          <Route
                            exact
                            path={`${match.path}/${routes.embarkDebarkPortUpdateReports}`}
                            component={EmbarkDebarkPortUpdateReport}
                          />
                        )}
                      {shouldShowBoardingSlotHistoryReportList && (
                        <Route
                          exact
                          path={`${match.path}/${routes.boardingSlotHistoryReports}`}
                          component={BoardingSlotHistoryReport}
                        />
                      )}
                      {shouldShowBoardingSlotReport && (
                        <Route
                          exact
                          path={`${match.path}/${routes.boardingSlotReport}`}
                          component={BoardingSlotReport}
                        />
                      )}
                      {shouldShowReportList && shouldShowCbpReportList && (
                        <Route
                          exact
                          path={`${match.path}/${routes.cbpReports}`}
                          component={CbpReport}
                        />
                      )}
                      {shouldShowReportList && shouldShowIpmReportList && (
                        <Route
                          exact
                          path={`${match.path}/${routes.ipmReports}`}
                          component={IpmReport}
                        />
                      )}
                      {shouldShowReportList && shouldShowFidelioReportList && (
                        <Route
                          exact
                          path={`${match.path}/${routes.fidelioReports}`}
                          component={FidelioReports}
                        />
                      )}
                      {shouldShowReportList &&
                        shouldShowPassengersWithAlertsReportList && (
                          <Route
                            exact
                            path={`${match.path}/${routes.passengersWithAlertsReports}`}
                            component={PassengersWithAlertsReports}
                          />
                        )}
                      {shouldShowReportList &&
                        shouldShowOnboardGuestWithPastDebarkDateReport && (
                          <Route
                            exact
                            path={`${match.path}/${routes.onboardGuestWithPastDebarkDateReport}`}
                            component={OnboardGuestWithPastDebarkDateReport}
                          />
                        )}
                      {shouldShowCbpDashboard && (
                        <>
                          {' '}
                          <Route
                            exact
                            path={`${match.path}/${routes.cbpDashboard}`}
                            component={CbpDashboard}
                          />
                          <Route
                            exact
                            path={`${match.path}/${routes.cbpMessageSchedule}`}
                            component={CbpMessageSchedule}
                          />
                          <Route
                            exact
                            path={`${match.path}/${routes.cbpVcoMessages}`}
                            component={CbpVcoMessage}
                          />
                          <Route
                            exact
                            path={`${match.path}/${routes.cbpCompareEnoad}`}
                            component={CbpComparison}
                          />
                        </>
                      )}
                      <Route
                        path="*"
                        component={
                          isDefaultRouteVMEnable ? Visitors : Dashboard
                        }
                      />{' '}
                      {/** 404 Route */}
                    </Switch>
                  </Suspense>
                </div>
              </div>
            </>
          )}
      </React.Fragment>
    </div>
  )
}

/** PropTypes */
App.propTypes = {
  loader: PropTypes.number.isRequired,
  auth: PropTypes.shape({}).isRequired,
  startup: PropTypes.shape({
    shipCode: PropTypes.string
  }).isRequired,
  retrieveStateroomData: PropTypes.shape({}).isRequired,
  match: PropTypes.shape({
    path: PropTypes.string
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  actions: PropTypes.shape({
    auth: PropTypes.func.isRequired,
    stateroom: PropTypes.func.isRequired,
    masterdata: PropTypes.func.isRequired,
    configs: PropTypes.func.isRequired,
    setActiveVoyage: PropTypes.func.isRequired,
    isPastVoyage: PropTypes.func.isRequired,
    getShips: PropTypes.func.isRequired,
    setMode: PropTypes.func.isRequired,
    getResources: PropTypes.func.isRequired,
    setActiveShip: PropTypes.func.isRequired,
    getSlotsData: PropTypes.func.isRequired,
    getGlobalRoles: PropTypes.func.isRequired
  }).isRequired,
  masterdata: PropTypes.shape({}).isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired,
  activeVoyage: PropTypes.shape({
    voyageName: PropTypes.string,
    embarkDate: PropTypes.string,
    debarkDate: PropTypes.string
  }).isRequired,
  activeShip: PropTypes.string.isRequired,
  isShore: PropTypes.bool.isRequired,
  ships: PropTypes.array.isRequired,
  activeShipName: PropTypes.string,
  settings: PropTypes.shape({}).isRequired,
  latestBoardingSlot: PropTypes.shape({}),
  totalActiveSlotsCount: PropTypes.number,
  shipTimes: PropTypes.shape({}).isRequired
}

/**
 * mapStateToProps
 * @param {object} state Redux State
 */
const mapStateToProps = state => {
  return {
    auth: get(state, 'auth', {}),
    startup: get(state, 'startup', {}),
    retrieveStateroomData: get(state, 'stateroom', {}),
    loader: get(state, 'loader', 0),
    masterdata: get(state, 'masterdata', {}),
    activeVoyage: get(state, 'voyage', {}),
    activeShip: get(state, 'ship', '') || '',
    activeShipName: get(state.masterdata, 'shipDetails.shipName', ''),
    isShore: get(state, 'isShore', false),
    ships: get(state, 'ships', []),
    settings: get(state, 'settings', {}),
    latestBoardingSlot: get(state, 'slots.latestBoardingSlot', {}),
    totalActiveSlotsCount: get(state, 'slots.activeCount', 0),
    shipTimes: get(state.masterdata, 'shipTimes', {})
  }
}

/**
 * mapDispatchToProps
 * @param {object} dispatch Redux Dispatch
 */
const mapDispatchToProps = dispatch => {
  return {
    actions: bindActionCreators(
      {
        auth: authAction,
        stateroom: poststateroomData,
        masterdata: postMasterData,
        setActiveVoyage: getVoyage,
        configs: getSettingsData,
        getShips: getShipsData,
        setActiveShip: getShip,
        isPastVoyage,
        setMode: setModeAction,
        getResources: getResourcesData,
        getSlotsData,
        getGlobalRoles: getGlobalSearchRoles
      },
      dispatch
    )
  }
}

export default injectIntl(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(App))
)
